import { useMemo } from 'react';
import {
  useQueryClient as _useQueryClient,
  QueryClient,
} from '@tanstack/react-query';
import type {
  QueryKey,
  QueryFilters,
  InvalidateQueryFilters,
  InvalidateOptions,
  SetDataOptions,
  RefetchOptions,
} from '@tanstack/react-query';

// --- Types ---

type LegacyQueryKey = QueryKey | string | number;

/**
 * Full interface bridge for a seamless v3 -> v5 transition.
 */
export interface LegacyCompatibleClient
  extends Omit<
    QueryClient,
    | 'invalidateQueries'
    | 'removeQueries'
    | 'setQueriesData'
    | 'setQueryData'
    | 'refetchQueries'
    | 'getQueryData'
    | 'getQueriesData'
  > {
  invalidateQueries(
    filters?: LegacyQueryKey | InvalidateQueryFilters,
    options?: InvalidateOptions,
  ): Promise<void>;

  removeQueries(filters?: LegacyQueryKey | QueryFilters): void;

  refetchQueries(
    filters?: LegacyQueryKey | QueryFilters,
    options?: RefetchOptions,
  ): Promise<void>;

  getQueryData<TData = unknown>(queryKey: LegacyQueryKey): TData | undefined;

  getQueriesData<TData = unknown>(
    filters: LegacyQueryKey | QueryFilters,
  ): [QueryKey, TData | undefined][];

  setQueryData<TData>(
    queryKey: LegacyQueryKey,
    updater: TData | ((old: TData | undefined) => TData),
    options?: SetDataOptions,
  ): TData | undefined;

  setQueriesData<TData>(
    filters: LegacyQueryKey | QueryFilters,
    updater: TData | ((old: TData | undefined) => TData),
    options?: SetDataOptions,
  ): [QueryKey, TData | undefined][];
}

// --- Helpers ---

function normalizeQueryKey(key: LegacyQueryKey): QueryKey {
  return Array.isArray(key) ? key : [key];
}

function isLegacyKey(arg: any): arg is LegacyQueryKey {
  return (
    typeof arg === 'string' || typeof arg === 'number' || Array.isArray(arg)
  );
}

// --- Hook ---

export default function useQueryClient(): LegacyCompatibleClient {
  const queryClient = _useQueryClient();

  return useMemo(() => {
    return new Proxy(queryClient, {
      get(target, prop, receiver) {
        const originalValue = Reflect.get(target, prop, receiver);

        const interceptors = {
          invalidateQueries: (filters?: any, options?: any) => {
            const v5Filters = isLegacyKey(filters)
              ? { queryKey: normalizeQueryKey(filters) }
              : filters;
            return target.invalidateQueries(v5Filters, options);
          },
          removeQueries: (filters?: any) => {
            const v5Filters = isLegacyKey(filters)
              ? { queryKey: normalizeQueryKey(filters) }
              : filters;
            return target.removeQueries(v5Filters);
          },
          refetchQueries: (filters?: any, options?: any) => {
            const v5Filters = isLegacyKey(filters)
              ? { queryKey: normalizeQueryKey(filters) }
              : filters;
            return target.refetchQueries(v5Filters, options);
          },
          getQueryData: (key: any) => {
            return target.getQueryData(normalizeQueryKey(key));
          },
          getQueriesData: (filters: any) => {
            const v5Filters = isLegacyKey(filters)
              ? { queryKey: normalizeQueryKey(filters) }
              : filters;
            return target.getQueriesData(v5Filters);
          },
          setQueryData: (key: any, updater: any, options: any) => {
            return target.setQueryData(
              normalizeQueryKey(key),
              updater,
              options,
            );
          },
          setQueriesData: (filters: any, updater: any, options: any) => {
            const v5Filters = isLegacyKey(filters)
              ? { queryKey: normalizeQueryKey(filters) }
              : filters;
            return target.setQueriesData(v5Filters, updater, options);
          },
        };

        if (prop in interceptors) {
          return interceptors[prop as keyof typeof interceptors];
        }

        return typeof originalValue === 'function'
          ? originalValue.bind(target)
          : originalValue;
      },
    }) as unknown as LegacyCompatibleClient;
  }, [queryClient]);
}
